<template>
  <div class="max-w-700">
    <div>
      <div class="pricing-packages bg-white mb-10">
        <div class="package-selectors sticky flex flex-row pt-10 pb-15 z-10">
          <div class="absolute top-0 h-86 w-full bg-white z-20"></div>
          <div
            v-for="(packageDoc, packageIndex) in visiblePackageOptions"
            :key="`package-${packageDoc.id}-${packageIndex}`"
            class="relative w-1/3 px-5 z-30 tablet:px-10"
          >
            <h3
              class="package-selector font-medium text-base text-white text-center tracking-wide leading-tight px-10
              py-25 rounded-5 shadow-6dp select-none z-30 mobile-l:text-lg laptop:px-35"
              :class="{
                'bg-gray-400 cursor-not-allowed shadow-none': disabledPackageIds.includes(packageDoc.id)
                || (activePackage && activePackage.id === packageDoc.id),
                'bg-primary': (selectedPackage.id !== packageDoc.id && !disabledPackageIds.includes(packageDoc.id)),
                'bg-secondary': selectedPackage.id === packageDoc.id
                && ((activePackage && activePackage.id !== packageDoc.id) || !activePackage),
                'cursor-pointer': isEventUpgrade || isSubscriptionPlans,
              }"
              @click="onPackagePress(packageDoc)"
            >
              {{ packageDoc.fields.shortName || 'Package' }}
            </h3>
            <div
              v-show="selectedPackage.id === packageDoc.id"
              class="absolute left-0 right-0 m-auto z-40"
              :class="[selectedPackage.id === packageDoc.id ? 'arrow-down-secondary' : 'arrow-down-primary']"
            ></div>
          </div>
        </div>
        <p class="flex items-center justify-center py-10 border-gray-200 laptop:justify-start laptop:pb-20
        laptop:border-b">
          {{ packageTypePriceLabel }}
        </p>
        <div class="flex flex-row font-medium text-xl text-primary text-center laptop:border-b
        laptop:border-gray-200 mobile-m:text-2xl">
          <p
            v-for="(packageDoc, packageIndex) in visiblePackageOptions"
            :key="`package-id-${packageDoc.id}`"
            class="w-1/3 flex items-center justify-center py-8 border-gray-200 laptop:pt-10 laptop:pb-20
            laptop:border-none"
            :class="{
              'border-l': packageIndex >= 1 && packageIndex < visiblePackageOptions.length - 1,
              'border-r': packageIndex >= 1 && packageIndex < visiblePackageOptions.length - 1,
              'cursor-not-allowed opacity-25': disabledPackageIds.includes(packageDoc.id)
                || (activePackage && activePackage.id === packageDoc.id),
              'text-gray-600': packageDoc.id !== selectedPackage.id,
              'font-bold text-secondary': packageDoc.id === selectedPackage.id
                && !disabledPackageIds.includes(packageDoc.id),
              'cursor-pointer': isEventUpgrade || isSubscriptionPlans,
            }"
            @click="onPackagePress(packageDoc)"
          >
            {{ formatPricing(packageDoc) }}
          </p>
        </div>
        <template
          v-for="(packageFeature, featureIndex) in packageFeatures"
        >
          <p
            :key="`feature-${packageFeature.feature}`"
            class="flex flex-col items-center justify-center pt-15 laptop:items-start laptop:py-10"
            :class="{ 'laptop:border-b laptop:border-gray-200': featureIndex === 0 }"
          >
            {{ packageFeature.label }}
          </p>
          <div
            :key="`feature-${featureIndex}`"
            class="flex flex-row text-lg text-primary-light text-center py-10 laptop:py-10"
            :class="{ 'laptop:border-b laptop:border-gray-200': featureIndex === 0 }"
          >
            <p
              v-for="(packageDoc, packageIndex) in visiblePackageOptions"
              :key="`package-id-${packageDoc.id}`"
              class="w-1/3 py-8 border-gray-200 laptop:border-none"
              :class="{
                'border-l': packageIndex >= 1 && packageIndex < visiblePackageOptions.length - 1,
                'border-r': packageIndex >= 1 && packageIndex < visiblePackageOptions.length - 1,
                'cursor-not-allowed opacity-25': disabledPackageIds.includes(packageDoc.id)
                || (activePackage && activePackage.id === packageDoc.id),
                'text-gray-600': packageDoc.id !== selectedPackage.id,
                'font-bold text-secondary': packageDoc.id === selectedPackage.id
                && !disabledPackageIds.includes(packageDoc.id),
                'cursor-pointer': isEventUpgrade || isSubscriptionPlans,
              }"
              @click="onPackagePress(packageDoc)"
            >
              {{ packageDoc.fields.features[packageFeature.feature].label }}
            </p>
          </div>
        </template>
      </div>

      <TransitionCollapse>
        <p
          v-if="isSubscriptionPlans"
          class="max-w-800 text-sm text-gray-600 text-center leading-tight py-20 px-25 bg-gray-100 rounded-5"
        >
          You are subscribing to our plans for a <span
          class="font-bold">minimum period of 3 months</span>.
          Your subscription will be renewed automatically if you don't cancel it before the end of
          the period.
        </p>
      </TransitionCollapse>

      <div
        v-if="!isEventUpgrade && !isSubscriptionPlans"
        class="max-w-800 flex flex-col items-center justify-between p-20 bg-gray-100 shadow-2dp rounded-5
        tablet:flex-row"
      >
        <p class="font-medium text-gray-700 leading-tight mb-20 tablet:mb-0 tablet:mr-30">
          Running several events over the next 3 months? Benefit from our subscription plans
        </p>
        <button
          class="flex-shrink-0 flex flex-row items-center px-30 py-14 border border-gray-500 rounded-5 cursor-pointer
          select-none transition-all duration-300 ease-in-out-quart focus:outline-none tablet:px-30"
          :class="{
            'hover:bg-gray-300': !subscriptionPlansAreVisible,
            'bg-primary hover:opacity-90': subscriptionPlansAreVisible,
          }"
          @click="onBecomeSubscriberPress"
        >
          <FontAwesomeIcon
            :icon="['fal', 'circle']"
            fixed-width
            class="text-2xl mr-15"
            :class="{
              'text-gray-600': !subscriptionPlansAreVisible,
              'text-primary-dark': subscriptionPlansAreVisible,
            }"
          />
          <Transition
            name="fade-scale"
            appear
          >
            <FontAwesomeIcon
              v-if="subscriptionPlansAreVisible"
              :icon="['fas', 'check-circle']"
              fixed-width
              class="absolute text-2xl text-white mr-15"
            />
          </Transition>
          <span
            class="font-medium leading-tight"
            :class="{
              'text-gray-600': !subscriptionPlansAreVisible,
              'text-white': subscriptionPlansAreVisible,
            }"
          >
            Show plans
          </span>
        </button>
      </div>
    </div>

    <div class="mt-20">
      <FeaturesList :features="standardEventorFeatures">
        Included features
      </FeaturesList>
    </div>

    <p
      v-if="isEventUpgrade"
      class="text-sm text-center text-gray-700 mt-40 mb-20"
    >
      Want to become a subscriber?
      <RouterLink
        :to="{ name: 'SubscriptionPlans' }"
        class="font-bold hover:underline"
      >
        See our subscription plans
      </RouterLink>
    </p>
    <p
      v-else
      class="text-sm text-center text-gray-700 mt-40 mb-20"
    >
      Need per event pricing? Press the "Upgrade" button
      on the existing event or create a new event.
    </p>

    <div
      v-if="selectedPackage.id || (!isEventUpgrade && !isSubscriptionPlans)"
      class="sticky bottom-0 w-full bg-white mt-15 py-10"
    >
      <button
        class="max-w-350 w-full flex flex-row items-center justify-center text-white text-center m-auto py-12
        bg-secondary rounded-5 shadow-2dp focus:outline-none"
        @click="onContinuePress"
      >
        <slot />
      </button>
    </div>
  </div>
</template>

<script>
import { defaultCurrencyCode, getSystemLocale } from '@/utils/intl';
import { standardEventorFeatures } from '@/config/packagesConfig';
import TransitionCollapse from '@/components/transition/TransitionCollapse';
import FeaturesList from '@/components/FeaturesList';

const packageTypeOneOff = 'oneOff';
const packageTypeSubscriptionPlan = 'subscriptionPlan';

export default {
  props: {
    type: {
      type: String,
      default: '',
    },
    currencyCode: {
      type: String,
      default: defaultCurrencyCode,
    },
    activePackage: {
      type: Object,
      default: null,
    },
    disabledPackageIds: {
      type: Array,
      default() {
        return [];
      },
    },
    packages: {
      type: Array,
      default() {
        return [];
      },
    },
  },
  components: {
    TransitionCollapse,
    FeaturesList,
  },
  data() {
    const packageFeatures = [{
      label: 'Max. guests',
      feature: 'numberOfGuests',
    }, {
      label: 'Max. devices',
      feature: 'numberOfCheckInDevices',
    }];
    if (this.type === 'subscription-plans') {
      packageFeatures.push({
        label: 'Max. events',
        feature: 'numberOfEvents',
      });
    }

    return {
      standardEventorFeatures,
      packageFeatures,
      selectedPackage: {
        id: '',
        type: packageTypeOneOff, // By default show oneOffs
        currencyCode: '',
      },
      navigatorLocale: getSystemLocale(),
    };
  },
  created() {
    if (!this.isSubscriptionPlans) return;

    this.selectedPackage.type = packageTypeSubscriptionPlan;
  },
  watch: {
    visiblePackageOptions: {
      immediate: true,
      handler(newValue) {
        if (!newValue.length) return;

        // For the pricing landing page
        if (!this.isEventUpgrade && !this.isSubscriptionPlans) {
          return;
        }
        if (!this.activePackage && newValue.length === 3) {
          this.selectedPackage.id = newValue[1].id;
          this.setPackageCurrencyCode(newValue[1]);
          return;
        }

        let i = this.visiblePackageOptions.length - 1;

        while (i >= 0) {
          if (this.disabledPackageIds.includes(this.visiblePackageOptions[i].id)
            || this.visiblePackageOptions[i].id === this.activePackage.id) break;

          this.selectedPackage.id = this.visiblePackageOptions[i].id;
          this.setPackageCurrencyCode(this.visiblePackageOptions[i]);
          i -= 1;
        }
      },
    },
  },
  computed: {
    isEventUpgrade() {
      return this.type === 'event-upgrade';
    },
    isSubscriptionPlans() {
      return this.type === 'subscription-plans';
    },
    visiblePackageOptions() {
      return this.packages.filter((packageDoc) => packageDoc.fields.type === this.selectedPackage.type);
    },
    subscriptionPlansAreVisible() {
      return this.selectedPackage.type === packageTypeSubscriptionPlan;
    },
    packageTypePriceLabel() {
      return this.selectedPackage.type === packageTypeOneOff ? 'Price for one Event' : 'Price per month';
    },
  },
  methods: {
    formatPricing(packageDoc) {
      let price = 0;
      let currencyCode = 'usd';

      if (!packageDoc.fields.pricings[this.currencyCode]) {
        if (!packageDoc.fields.pricings.usd || !packageDoc.fields.pricings.usd.value) return '-';

        price = packageDoc.fields.pricings.usd.value;
      } else {
        price = packageDoc.fields.pricings[this.currencyCode].value;
        ({ currencyCode } = this);
      }

      // Event upgrade from existing package
      if (this.activePackage) {
        if (!this.activePackage.fields.pricings[this.selectedPackage.currencyCode || this.currencyCode]) {
          return '-';
        }

        if (this.packageUpgradeIsAllowed(packageDoc.id)) {
          price -= this.activePackage.fields.pricings[this.selectedPackage.currencyCode || this.currencyCode].value;
        }
      }

      return (price / 100).toLocaleString(this.navigatorLocale, {
        style: 'currency',
        currency: currencyCode,
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      });
    },
    packageUpgradeIsAllowed(packageId) {
      return !this.disabledPackageIds.includes(packageId)
        && ((this.activePackage && this.activePackage.id !== packageId) || !this.activePackage);
    },
    onPackagePress(packageDoc) {
      if (!this.isEventUpgrade && !this.isSubscriptionPlans) return;
      if (!this.packageUpgradeIsAllowed(packageDoc.id)) return;

      this.selectedPackage.id = packageDoc.id;
      this.setPackageCurrencyCode(packageDoc);
    },
    setPackageCurrencyCode(packageDoc) {
      let currencyCode = defaultCurrencyCode;

      if (packageDoc.fields.pricings[this.currencyCode] && packageDoc.fields.pricings[this.currencyCode].value) {
        ({ currencyCode } = this);
      }

      this.selectedPackage.currencyCode = currencyCode;
    },
    onBecomeSubscriberPress() {
      if (this.selectedPackage.type === packageTypeOneOff) {
        this.selectedPackage.type = packageTypeSubscriptionPlan;
      } else {
        this.selectedPackage.type = packageTypeOneOff;
      }
    },
    onContinuePress() {
      this.$emit('continue', {
        packageId: this.selectedPackage.id,
        currencyCode: this.selectedPackage.currencyCode || this.currencyCode,
      });
    },
  },
};
</script>

<style scoped>
.pricing-packages {
  display: grid;
  grid-template-columns: auto auto;

  .package-selectors {
    grid-column: 2;
    top: var(--header-height);

    .package-selector {
      transition: opacity 0.3s;
    }
  }
}

@screen touch {
  .pricing-packages {
    grid-template-columns: auto;

    .package-selectors {
      @apply top-header;

      grid-column: 1;
    }
  }
}

@screen laptop {
  .pricing-packages .package-selectors {
    @apply relative top-0;
  }
}

.arrow-down-secondary {
  @apply w-0 h-0;

  border-left: 18px solid transparent;
  border-right: 18px solid transparent;
  border-top: 18px solid theme('colors.secondary.default');
}

.arrow-down-primary {
  @apply w-0 h-0;

  border-left: 14px solid transparent;
  border-right: 14px solid transparent;
  border-top: 14px solid theme('colors.primary.default');
}
</style>
